import React, { useState, useEffect } from 'react';
import { useShoppingListItems } from '../hooks/useShoppingListItems';
import { usePantryItems } from '../hooks/usePantryItems';
import {
  addShoppingListItem,
  updateShoppingListItem,
  deleteShoppingListItem,
  markShoppingListItemAsPurchased,
  addShoppingListItemToPantry,
  clearAllShoppingListItems,
  completeShopping,
} from '../lib/shoppingListService';
import ShoppingListItemForm from '../components/ShoppingListItemForm';
import ShoppingListItemList from '../components/ShoppingListItemList';
import ConfirmationDialog from '../components/ConfirmationDialog';
import { db } from '../lib/firebaseClient';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useToast } from '../components/Toast';

const ShoppingListPage = () => {
  const { items, loading, error } = useShoppingListItems({ showPurchased: true });
  const { items: pantryItems } = usePantryItems();
  const { showToast } = useToast();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [taxRate, setTaxRate] = useState(0.0825); // Default CA tax rate (8.25%)
  const [taxRateLoading, setTaxRateLoading] = useState(true);
  const [showTaxSettings, setShowTaxSettings] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, itemId: null });
  const [clearAllConfirm, setClearAllConfirm] = useState(false);

  // Load tax rate from Firestore settings
  useEffect(() => {
    const loadTaxRate = async () => {
      try {
        const settingsRef = doc(db, 'settings', 'tax_rate');
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          setTaxRate(data.taxRate || 0.0825);
        }
      } catch (error) {
        console.error('Error loading tax rate:', error);
      } finally {
        setTaxRateLoading(false);
      }
    };
    loadTaxRate();
  }, []);

  // Save tax rate to Firestore
  const saveTaxRate = async (newRate) => {
    try {
      const settingsRef = doc(db, 'settings', 'tax_rate');
      await setDoc(settingsRef, {
        taxRate: newRate,
        state: 'CA',
        updatedAt: new Date(),
      }, { merge: true });
      setTaxRate(newRate);
      showToast('Tax rate saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving tax rate:', error);
      showToast('Failed to save tax rate: ' + error.message, 'error');
    }
  };

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && (showAddForm || editingItem)) {
        setShowAddForm(false);
        setEditingItem(null);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showAddForm, editingItem]);

  const handleAddItem = async (itemData) => {
    try {
      await addShoppingListItem(itemData);
      setShowAddForm(false);
      showToast('Item added successfully!', 'success');
    } catch (error) {
      console.error('Error adding item:', error);
      showToast('Failed to add item: ' + error.message, 'error');
    }
  };

  const handleUpdateItem = async (itemId, itemData) => {
    try {
      await updateShoppingListItem(itemId, itemData);
      setEditingItem(null);
      showToast('Item updated successfully!', 'success');
    } catch (error) {
      console.error('Error updating item:', error);
      showToast('Failed to update item: ' + error.message, 'error');
    }
  };

  const handleDeleteItem = (itemId) => {
    setDeleteConfirm({ isOpen: true, itemId });
  };

  const confirmDeleteItem = async () => {
    const itemId = deleteConfirm.itemId;
    setDeleteConfirm({ isOpen: false, itemId: null });
    try {
      await deleteShoppingListItem(itemId);
      showToast('Item deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting item:', error);
      showToast('Failed to delete item: ' + error.message, 'error');
    }
  };

  const handleMarkAsPurchased = async (itemId) => {
    try {
      await markShoppingListItemAsPurchased(itemId);
      // No toast for marking as purchased to avoid spam
    } catch (error) {
      console.error('Error marking as purchased:', error);
      showToast('Failed to mark as purchased: ' + error.message, 'error');
    }
  };

  const handleAddToPantry = async (itemId) => {
    try {
      await addShoppingListItemToPantry(itemId);
      showToast('Item added to pantry!', 'success');
    } catch (error) {
      console.error('Error adding to pantry:', error);
      showToast('Failed to add to pantry: ' + error.message, 'error');
    }
  };

  const handleCopyList = async () => {
    const activeItems = items.filter((item) => !item.isPurchased);
    const itemsToCopy = activeItems.length > 0 ? activeItems : items;

    if (itemsToCopy.length === 0) {
      showToast('Shopping list is empty', 'info');
      return;
    }

    const lines = itemsToCopy.map((item) => {
      const qty = item.quantity && item.quantity !== 1 ? ` (${item.quantity}${item.unit ? ` ${item.unit}` : ''})` : '';
      return `- ${item.name}${qty}`;
    });

    const text = `Shopping List\n${lines.join('\n')}`;

    try {
      await navigator.clipboard.writeText(text);
      showToast(`Copied ${itemsToCopy.length} item(s) to clipboard`, 'success');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      showToast('Failed to copy list: ' + error.message, 'error');
    }
  };

  const handleClearAll = () => {
    if (items.length === 0) {
      showToast('Shopping list is already empty', 'info');
      return;
    }
    setClearAllConfirm(true);
  };

  const confirmClearAll = async () => {
    setClearAllConfirm(false);
    try {
      await clearAllShoppingListItems();
      showToast('Shopping list cleared! Ready for your next shopping trip.', 'success');
    } catch (error) {
      console.error('Error clearing shopping list:', error);
      showToast('Failed to clear shopping list: ' + error.message, 'error');
    }
  };

  const handleCompleteShopping = async () => {
    setIsCompleting(true);
    try {
      const result = await completeShopping(items);
      setShowCompleteDialog(false);
      
      let message = `Shopping completed! `;
      if (result.itemsProcessed > 0) {
        message += `Added ${result.itemsProcessed} item(s) to pantry. `;
      }
      if (result.itemsFailed > 0) {
        message += `Warning: ${result.itemsFailed} item(s) failed to process. `;
      }
      message += `Shopping list cleared.`;
      
      const toastType = result.itemsFailed > 0 ? 'warning' : 'success';
      showToast(message, toastType);
    } catch (error) {
      console.error('Error completing shopping:', error);
      showToast('Failed to complete shopping: ' + error.message, 'error');
    } finally {
      setIsCompleting(false);
    }
  };

  // Calculate totals
  const calculateSubtotal = () => {
    return items.reduce((sum, item) => {
      const quantity = item.quantity || 1;
      const price = item.price || 0;
      return sum + (quantity * price);
    }, 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * taxRate;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (loading || taxRateLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
          <p className="mt-4 text-lg text-gray-600">Loading shopping list...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-md m-4">
        Error loading shopping list: {error.message}
      </div>
    );
  }

  const subtotal = calculateSubtotal();
  const tax = calculateTax();
  const total = calculateTotal();

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <h1 className="text-2xl sm:text-3xl font-bold text-purple-700">
              Shopping List
            </h1>
            <div className="flex flex-wrap gap-2">
              {items.length > 0 && (
                <>
                  <button
                    onClick={handleCopyList}
                    className="bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 text-sm font-medium"
                  >
                    📋 Copy List
                  </button>
                  <button
                    onClick={() => setShowCompleteDialog(true)}
                    className="bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 text-sm font-medium"
                  >
                    ✓ Complete Shopping
                  </button>
                  <button
                    onClick={handleClearAll}
                    className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm font-medium"
                  >
                    🗑️ Clear All
                  </button>
                </>
              )}
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700"
              >
                + Add Item
              </button>
            </div>
          </div>
        </div>

        {/* Add/Edit Modal */}
        {(showAddForm || editingItem) && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowAddForm(false);
                setEditingItem(null);
              }
            }}
          >
            <div 
              className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingItem ? 'Edit Item' : 'Add Item'}
                </h2>
                <button
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingItem(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="p-6">
                <ShoppingListItemForm
                  item={editingItem}
                  pantryItems={pantryItems}
                  onSave={editingItem
                    ? (data) => handleUpdateItem(editingItem.id, data)
                    : handleAddItem}
                  onCancel={() => {
                    setShowAddForm(false);
                    setEditingItem(null);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Shopping List Items */}
        <div className="mb-6">
          <ShoppingListItemList
            items={items}
            onEdit={setEditingItem}
            onDelete={handleDeleteItem}
            onMarkAsPurchased={handleMarkAsPurchased}
            onAddToPantry={handleAddToPantry}
          />
        </div>

        {/* Cost Summary */}
        {items.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Cost Summary</h2>
              <button
                onClick={() => setShowTaxSettings(!showTaxSettings)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                {showTaxSettings ? 'Hide' : '⚙️'} Tax Settings
              </button>
            </div>
            
            {/* Collapsible Tax Settings */}
            {showTaxSettings && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md border border-gray-200">
                <label htmlFor="tax-rate" className="block text-sm font-medium text-gray-700 mb-2">
                  Tax Rate (CA): {taxRateLoading ? 'Loading...' : `${(taxRate * 100).toFixed(2)}%`}
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    id="tax-rate"
                    type="number"
                    min="0"
                    max="1"
                    step="0.0001"
                    value={taxRate}
                    onChange={(e) => {
                      const newRate = parseFloat(e.target.value) || 0;
                      if (newRate >= 0 && newRate <= 1) {
                        setTaxRate(newRate);
                      }
                    }}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                  />
                  <button
                    onClick={() => {
                      saveTaxRate(taxRate);
                      setShowTaxSettings(false);
                    }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 text-sm"
                  >
                    Save
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Enter as decimal (e.g., 0.0825 for 8.25%)
                </p>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex justify-between text-sm sm:text-base">
                <span className="text-gray-700">Subtotal:</span>
                <span className="font-medium">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm sm:text-base">
                <span className="text-gray-700">
                  Tax ({(taxRate * 100).toFixed(2)}%):
                </span>
                <span className="font-medium">{formatCurrency(tax)}</span>
              </div>
              <div className="border-t border-gray-200 pt-2 flex justify-between">
                <span className="text-base sm:text-lg font-semibold text-gray-900">Total:</span>
                <span className="text-base sm:text-lg font-bold text-purple-600">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        )}

        {/* Complete Shopping Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={showCompleteDialog}
          onClose={() => setShowCompleteDialog(false)}
          onConfirm={handleCompleteShopping}
          title="Complete Shopping?"
          message={
            <>
              This will:
              <ul className="list-disc list-inside mt-2 mb-2 space-y-1 text-left">
                <li>Mark all unpurchased items as purchased</li>
                <li>Add all items to your pantry inventory</li>
                <li>Clear your shopping list for the next trip</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mt-3">
                <p className="text-sm text-blue-800">
                  <strong>{items.filter(item => !item.isPurchased).length}</strong> item(s) will be added to pantry
                </p>
              </div>
            </>
          }
          confirmText={isCompleting ? 'Processing...' : 'Complete Shopping'}
          cancelText="Cancel"
          confirmButtonColor="bg-green-600 hover:bg-green-700"
          isLoading={isCompleting}
          variant="info"
        />

        {/* Delete Item Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, itemId: null })}
          onConfirm={confirmDeleteItem}
          title="Delete Item"
          message="Are you sure you want to delete this item? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />

        {/* Clear All Confirmation Dialog */}
        <ConfirmationDialog
          isOpen={clearAllConfirm}
          onClose={() => setClearAllConfirm(false)}
          onConfirm={confirmClearAll}
          title="Clear Shopping List"
          message={`Are you sure you want to clear all ${items.length} item(s) from your shopping list? This will start a fresh list for your next shopping trip.`}
          confirmText="Clear All"
          cancelText="Cancel"
          variant="warning"
        />
      </div>
    </div>
  );
};

export default ShoppingListPage;

